import type { Quat, Vec3 } from './math';

/** Above this jump between two physics steps (m) the pose is a teleport (respawn, reset): no blending across it. */
export const TELEPORT = 2;

/**
 * The pose drawn between two 240 Hz physics steps: `alpha` (0..1) of the way from the previous step to the current.
 * Without it a frame shows whole steps only, so at 45 fps the bike moved 5 or 6 steps per frame (a 20 % jitter,
 * ~7 cm at 60 km/h) and the ride looked jerky (owner 2026-10-05). Writes into `outP`/`outQ`.
 */
export function blendPose(prevP: Vec3, prevQ: Quat, curP: Vec3, curQ: Quat, alpha: number, outP: Vec3, outQ: Quat): void {
  const dx = curP.x - prevP.x, dy = curP.y - prevP.y, dz = curP.z - prevP.z;
  const a = dx * dx + dy * dy + dz * dz > TELEPORT * TELEPORT ? 1 : Math.min(1, Math.max(0, alpha));
  outP.x = prevP.x + dx * a; outP.y = prevP.y + dy * a; outP.z = prevP.z + dz * a;
  // Normalised lerp on the shorter arc (two steps apart the angle is tiny, so it matches slerp).
  const s = prevQ.x * curQ.x + prevQ.y * curQ.y + prevQ.z * curQ.z + prevQ.w * curQ.w < 0 ? -1 : 1;
  const x = prevQ.x + (curQ.x * s - prevQ.x) * a, y = prevQ.y + (curQ.y * s - prevQ.y) * a;
  const z = prevQ.z + (curQ.z * s - prevQ.z) * a, w = prevQ.w + (curQ.w * s - prevQ.w) * a;
  const n = Math.hypot(x, y, z, w) || 1;
  outQ.x = x / n; outQ.y = y / n; outQ.z = z / n; outQ.w = w / n;
}
